import { AWIN_ADVERTISER_ID } from './awinConfig';

const LIVE_HOST = 'app.co2impact.co.uk';
const MASTER_TAG_ID = 'awin-master-tag';

let paymentContextCount = 0;
let masterTagLoadPromise: Promise<boolean> | null = null;

function isLiveHost(): boolean {
    return window.location.hostname === LIVE_HOST;
}

function removeMasterTag(): void {
    document.getElementById(MASTER_TAG_ID)?.remove();
    masterTagLoadPromise = null;
}

function loadMasterTag(): Promise<boolean> {
    if (!isLiveHost() || paymentContextCount > 0 || document.getElementById(MASTER_TAG_ID)) {
        return Promise.resolve(Boolean(window.AWIN?.Tracking) && paymentContextCount === 0);
    }

    if (masterTagLoadPromise) {
        return masterTagLoadPromise;
    }

    masterTagLoadPromise = new Promise((resolve) => {
        const script = document.createElement('script');
        script.id = MASTER_TAG_ID;
        script.src = `https://www.dwin1.com/${AWIN_ADVERTISER_ID}.js`;
        script.type = 'text/javascript';
        script.defer = true;
        script.onload = () => resolve(Boolean(window.AWIN?.Tracking));
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });

    return masterTagLoadPromise;
}

/**
 * Loads Awin's MasterTag only on the production SPA and only while no payment
 * UI is active. Stripe Elements components call setAwinPaymentContext() for
 * their lifetime so Awin's tag is absent wherever card/payment data is shown
 * or processed.
 */
export function initializeAwinMasterTag(): void {
    if (!isLiveHost()) return;

    // Let React commit first: payment components set their Awin payment context in
    // useLayoutEffect, before the first paint. This avoids adding the MasterTag on
    // an initial Stripe Elements page even briefly.
    const scheduleLoad = () => window.requestAnimationFrame(() => {
        void loadMasterTag();
    });

    if (document.body) {
        scheduleLoad();
    } else {
        document.addEventListener('DOMContentLoaded', scheduleLoad, { once: true });
    }
}

export function setAwinPaymentContext(active: boolean): void {
    if (!isLiveHost()) return;

    paymentContextCount = Math.max(0, paymentContextCount + (active ? 1 : -1));

    if (paymentContextCount > 0) {
        removeMasterTag();
    } else {
        void loadMasterTag();
    }
}

/**
 * Gives a confirmation page a short opportunity to use the MasterTag after a
 * Stripe Elements screen has unmounted. The fallback conversion pixel remains
 * available if the external script is blocked or slower than this timeout.
 */
export async function waitForAwinMasterTag(timeoutMs = 2000): Promise<boolean> {
    if (!isLiveHost() || paymentContextCount > 0) {
        return false;
    }

    const loaded = loadMasterTag();
    const timeout = new Promise<boolean>((resolve) => {
        window.setTimeout(() => resolve(false), timeoutMs);
    });

    return Promise.race([loaded, timeout]);
}
